import type {
  FreebeManualFileInspection,
  FreebeManualImportReceipt,
  FreebeManualImportFamily,
  FreebeSourcePeriod,
} from '@tosho/contracts';
export type FreebeConnectionStatus =
  'PENDING_AUTHORIZATION' | 'AUTHORIZATION_CAPTURED' | 'ACTIVE' | 'DISABLED';
export type FreebeAuthorizationStatus =
  | 'QUEUED'
  | 'STARTING_BROWSER'
  | 'AWAITING_USER'
  | 'VERIFYING'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED'
  | 'EXPIRED';

export interface FreebeAuthorizationAttemptResult {
  readonly id: string;
  readonly status: FreebeAuthorizationStatus;
  readonly failureCode: string | null;
  readonly expiresAt: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}
export type FreebeImportRunStatus =
  | 'QUEUED'
  | 'ACQUIRING'
  | 'PREFLIGHT_BLOCKED'
  | 'PREFLIGHT_REVIEW_REQUIRED'
  | 'PREFLIGHT_CHECKED'
  | 'CANCELLED'
  | 'FAILED'
  | 'COMPLETED';

export interface FreebeConnectionResult {
  readonly id: string;
  readonly status: FreebeConnectionStatus;
  readonly authenticationMode: 'EMAIL_PASSWORD';
  readonly accountEmail: string | null;
  readonly authorizedAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export type FreebeDatasetFamily =
  | 'CLIENTS'
  | 'CONTACTS'
  | 'SUPPLIERS'
  | 'PURCHASES'
  | 'SALES'
  | 'QUOTES'
  | 'BALANCE'
  | 'OPERATIONS'
  | 'MOVEMENTS';

export interface FreebeDatasetSummaryResult {
  readonly sourceSynchronizedAt?: string | null;
  readonly family: FreebeDatasetFamily;
  readonly elementCount: number;
  readonly lastSuccessfulAt: string | null;
}

export interface FreebeMfaChallengeResult {
  readonly id: string;
  readonly runId: string;
  readonly status: 'WAITING' | 'VALIDATING';
  readonly expiresAt: string;
  readonly attemptCount: number;
}

export interface FreebeImportRunResult {
  readonly sourcePeriod?: FreebeSourcePeriod | null;
  readonly awaitingMfa?: boolean;
  readonly id: string;
  readonly family: FreebeDatasetFamily;
  readonly trigger: 'MANUAL_UPLOAD' | 'AUTOMATIC_SCHEDULED' | 'AUTOMATIC_ON_DEMAND';
  readonly status: FreebeImportRunStatus;
  readonly failureCode: string | null;
  readonly startedAt: string;
  readonly finishedAt: string | null;
  readonly report: {
    readonly status: 'BLOCKED' | 'REVIEW_REQUIRED' | 'REFERENCES_CHECKED';
    readonly fileCount: number;
    readonly rowCount: number;
    readonly errorCount: number;
    readonly warningCount: number;
    readonly duplicateOfReportId: string | null;
  } | null;
  readonly preview: {
    readonly status: 'BLOCKED' | 'REVIEW_REQUIRED' | 'READY';
    readonly rowCount: number;
    readonly createCount: number;
    readonly updateCount: number;
    readonly unchangedCount: number;
    readonly conflictCount: number;
    readonly missingFromSourceCount: number;
    readonly errorCount: number;
    readonly warningCount: number;
  } | null;
  readonly application: {
    readonly createCount: number;
    readonly updateCount: number;
    readonly unchangedCount: number;
    readonly appliedAt: string;
  } | null;
}

export interface FreebeIntegrationResult {
  readonly connection: FreebeConnectionResult | null;
  readonly authorization: FreebeAuthorizationAttemptResult | null;
  readonly datasets: readonly FreebeDatasetSummaryResult[];
  readonly history: readonly FreebeImportRunResult[];
}

export type FreebeManualImportResult = FreebeManualImportReceipt;

export type DeclarationImportMode = 'historical' | 'current_year';

export interface FreebeDeclarationPreviewResult {
  readonly archiveSha256: string;
  readonly declarationCount: number;
  readonly excludedOutsideCurrentYear: number;
  readonly missingPdf: number;
  readonly freebeDraftCount: number;
  readonly firstSourcePeriod: string | null;
  readonly lastSourcePeriod: string | null;
}

export interface FreebeDeclarationImportResult {
  readonly imported: number;
  readonly unchanged: number;
  readonly excludedOutsideCurrentYear: number;
  readonly missingPdf: number;
  readonly pendingOfficialReview: number;
}

export interface FreebeDeclarationImportReceipt {
  readonly id: string;
  readonly mode: 'historical' | 'current_year' | 'legacy';
  readonly imported: number;
  readonly unchanged: number;
  readonly completedAt: string;
}

export class FreebeIntegrationApiError extends Error {
  public constructor(
    public readonly status: number,
    public readonly code: string | null,
  ) {
    super("La demande d'intégration Freebe ne peut pas être traitée.");
    this.name = 'FreebeIntegrationApiError';
  }
}

async function request<T>(
  method: 'GET' | 'POST' | 'DELETE',
  path: string,
  csrfToken?: string,
  body?: unknown,
): Promise<T> {
  const response = await fetch(`/api/integrations/freebe${path}`, {
    method,
    credentials: 'same-origin',
    headers: {
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(csrfToken === undefined ? {} : { 'X-CSRF-Token': csrfToken }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!response.ok) await throwApiError(response);
  return (await response.json()) as T;
}

async function throwApiError(response: Response): Promise<never> {
  let code: string | null = null;
  try {
    const payload = (await response.json()) as unknown;
    if (typeof payload === 'object' && payload !== null && 'code' in payload) {
      const candidate = (payload as { code?: unknown }).code;
      code = typeof candidate === 'string' ? candidate : null;
    }
  } catch {
    // Le statut HTTP reste la source de vérité si le corps d'erreur est absent ou invalide.
  }
  throw new FreebeIntegrationApiError(response.status, code);
}

async function declarationArchiveRequest<T>(
  file: File,
  csrfToken: string,
  mode: DeclarationImportMode,
  preview: boolean,
  expectedSha256?: string,
): Promise<T> {
  const route = mode === 'historical' ? 'historical' : 'current-year';
  const response = await fetch(
    `/api/integrations/freebe/declarations/${preview ? 'preview/' : ''}${route}`,
    {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/zip',
        'X-CSRF-Token': csrfToken,
        ...(expectedSha256 === undefined ? {} : { 'X-Tosho-Archive-Sha256': expectedSha256 }),
      },
      body: file,
    },
  );
  if (!response.ok) await throwApiError(response);
  return (await response.json()) as T;
}

export const freebeIntegrationApi = {
  async readDeclarationHistory(): Promise<readonly FreebeDeclarationImportReceipt[]> {
    const response = await fetch('/api/integrations/freebe/declarations/history', {
      credentials: 'same-origin',
    });
    if (!response.ok) await throwApiError(response);
    return (await response.json()) as readonly FreebeDeclarationImportReceipt[];
  },
  previewDeclarationArchive(
    file: File,
    mode: DeclarationImportMode,
    csrfToken: string,
  ): Promise<FreebeDeclarationPreviewResult> {
    return declarationArchiveRequest(file, csrfToken, mode, true);
  },
  importDeclarationArchive(
    file: File,
    mode: DeclarationImportMode,
    archiveSha256: string,
    csrfToken: string,
  ): Promise<FreebeDeclarationImportResult> {
    return declarationArchiveRequest(file, csrfToken, mode, false, archiveSha256);
  },
  read(): Promise<FreebeIntegrationResult> {
    return request('GET', '');
  },
  pendingMfa(): Promise<readonly FreebeMfaChallengeResult[]> {
    return request('GET', '/mfa');
  },
  submitMfa(id: string, code: string, csrfToken: string): Promise<{ status: 'VALIDATING' }> {
    return request('POST', `/mfa/${encodeURIComponent(id)}/code`, csrfToken, { code });
  },
  saveDirectLoginProfile(accountEmail: string, csrfToken: string): Promise<FreebeConnectionResult> {
    return request('POST', '/connection', csrfToken, {
      authenticationMode: 'EMAIL_PASSWORD',
      accountEmail,
    });
  },
  disable(csrfToken: string): Promise<FreebeConnectionResult> {
    return request('DELETE', '/connection', csrfToken);
  },
  launchNow(
    family: FreebeDatasetFamily | 'ALL',
    csrfToken: string,
  ): Promise<FreebeImportRunResult> {
    const path = family === 'ALL' ? '/imports' : `/imports/${family.toLowerCase()}`;
    return request('POST', path, csrfToken);
  },
  async inspectManualFile(
    family: FreebeManualImportFamily,
    file: File,
    csrfToken: string,
    signal?: AbortSignal,
  ): Promise<FreebeManualFileInspection> {
    const format = family === 'quotes' || family === 'movements' ? 'xlsx' : 'json';
    const response = await fetch(`/api/integrations/freebe/imports/manual/${family}/inspect`, {
      method: 'POST',
      credentials: 'same-origin',
      ...(signal === undefined ? {} : { signal }),
      headers: {
        'Content-Type': `application/vnd.tosho.freebe-${family}+${format}`,
        'X-CSRF-Token': csrfToken,
      },
      body: file,
    });
    if (!response.ok) await throwApiError(response);
    return response.json() as Promise<FreebeManualFileInspection>;
  },
  async uploadClientsJson(file: File, csrfToken: string): Promise<FreebeManualImportResult> {
    const response = await fetch('/api/integrations/freebe/imports/manual/clients', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/vnd.tosho.freebe-clients+json',
        'X-CSRF-Token': csrfToken,
      },
      body: file,
    });
    if (!response.ok) await throwApiError(response);
    return (await response.json()) as FreebeManualImportResult;
  },
  async uploadContactsJson(file: File, csrfToken: string): Promise<FreebeManualImportResult> {
    const response = await fetch('/api/integrations/freebe/imports/manual/contacts', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/vnd.tosho.freebe-contacts+json',
        'X-CSRF-Token': csrfToken,
      },
      body: file,
    });
    if (!response.ok) await throwApiError(response);
    return (await response.json()) as FreebeManualImportResult;
  },
  async uploadSuppliersJson(file: File, csrfToken: string): Promise<FreebeManualImportResult> {
    const response = await fetch('/api/integrations/freebe/imports/manual/suppliers', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/vnd.tosho.freebe-suppliers+json',
        'X-CSRF-Token': csrfToken,
      },
      body: file,
    });
    if (!response.ok) await throwApiError(response);
    return (await response.json()) as FreebeManualImportResult;
  },
  async uploadPurchasesJson(file: File, csrfToken: string): Promise<FreebeManualImportResult> {
    const response = await fetch('/api/integrations/freebe/imports/manual/purchases', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/vnd.tosho.freebe-purchases+json',
        'X-CSRF-Token': csrfToken,
      },
      body: file,
    });
    if (!response.ok) await throwApiError(response);
    return (await response.json()) as FreebeManualImportResult;
  },
  async uploadSalesInvoicesJson(file: File, csrfToken: string): Promise<FreebeManualImportResult> {
    const response = await fetch('/api/integrations/freebe/imports/manual/sales', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/vnd.tosho.freebe-sales+json',
        'X-CSRF-Token': csrfToken,
      },
      body: file,
    });
    if (!response.ok) await throwApiError(response);
    return (await response.json()) as FreebeManualImportResult;
  },
  async uploadQuotesXlsx(file: File, csrfToken: string): Promise<FreebeManualImportResult> {
    const response = await fetch('/api/integrations/freebe/imports/manual/quotes', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/vnd.tosho.freebe-quotes+xlsx',
        'X-CSRF-Token': csrfToken,
      },
      body: file,
    });
    if (!response.ok) await throwApiError(response);
    return (await response.json()) as FreebeManualImportResult;
  },
  async uploadOperationsJson(file: File, csrfToken: string): Promise<FreebeManualImportResult> {
    const response = await fetch('/api/integrations/freebe/imports/manual/operations', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/vnd.tosho.freebe-operations+json',
        'X-CSRF-Token': csrfToken,
      },
      body: file,
    });
    if (!response.ok) await throwApiError(response);
    return (await response.json()) as FreebeManualImportResult;
  },
  async uploadMovementsXlsx(file: File, csrfToken: string): Promise<FreebeManualImportResult> {
    const response = await fetch('/api/integrations/freebe/imports/manual/movements', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/vnd.tosho.freebe-movements+xlsx',
        'X-CSRF-Token': csrfToken,
      },
      body: file,
    });
    if (!response.ok) await throwApiError(response);
    return (await response.json()) as FreebeManualImportResult;
  },
  cancelImport(runId: string, csrfToken: string): Promise<FreebeImportRunResult> {
    return request('DELETE', `/imports/${encodeURIComponent(runId)}`, csrfToken);
  },
  beginAuthorization(csrfToken: string): Promise<FreebeAuthorizationAttemptResult> {
    return request('POST', '/authorization', csrfToken);
  },
  verifyAuthorization(
    attemptId: string,
    csrfToken: string,
  ): Promise<FreebeAuthorizationAttemptResult> {
    return request('POST', `/authorization/${encodeURIComponent(attemptId)}/verify`, csrfToken);
  },
  cancelAuthorization(
    attemptId: string,
    csrfToken: string,
  ): Promise<FreebeAuthorizationAttemptResult> {
    return request('DELETE', `/authorization/${encodeURIComponent(attemptId)}`, csrfToken);
  },
};

/** HTTP success means the file was processed, not that it was applied. */
export function assertManualImportCompleted(result: FreebeManualImportResult): void {
  if (result.status !== 'COMPLETED')
    throw new FreebeIntegrationApiError(
      409,
      result.status === 'PREFLIGHT_REVIEW_REQUIRED'
        ? 'FREEBE_MANUAL_REVIEW_REQUIRED'
        : result.status === 'PREFLIGHT_BLOCKED'
          ? 'FREEBE_MANUAL_FILE_REJECTED'
          : 'FREEBE_MANUAL_NOT_APPLIED',
    );
}

export function manualImportErrorMessage(
  error: unknown,
  phase: 'import' | 'inspection' = 'import',
): string {
  if (error instanceof FreebeIntegrationApiError) {
    if (error.code === 'FREEBE_MANUAL_REVIEW_REQUIRED')
      return 'Aucune donnée modifiée : cet import nécessite une vérification. Consultez son rapport dans l’historique.';
    if (error.code === 'FREEBE_MANUAL_FILE_REJECTED' || error.code === 'FREEBE_MANUAL_FILE_INVALID')
      return 'Fichier refusé : vérifiez le format et la carte choisie. Aucune donnée importée.';
    if (error.status === 401 || error.status === 403)
      return 'La session ne permet pas cet import. Reconnectez-vous à Tosho puis réessayez.';
    if (error.code === 'FREEBE_MANUAL_NOT_APPLIED')
      return 'L’application des données n’est pas confirmée. Consultez l’historique avant de réessayer.';
  }
  return phase === 'inspection'
    ? 'L’analyse du fichier est indisponible. Vérifiez la connexion puis réessayez.'
    : 'Le résultat de l’import ne peut pas être confirmé. Consultez l’historique avant de réessayer.';
}
